import { useState, useEffect } from "react";
import Header from "../../header/Header";
import FadeIn from "../../../general/FadeIn";
import OrangeButton from "../../../general/OrangeButton";
import LeafletMap from "../../../general/Leaflet";
import Api from "../../../../services/Api";
import FalecidosModal from "../../../general/FalecidosModal";
import { QRCodeSVG } from "qrcode.react";
import { useResponsive } from "../../../../hooks/useResponsive";
import JazigoCarousel from "../../../general/JazigoCarousel";
import DateInput from "../../../general/DateInput";

// Painel de falecidos sobreposto ao mapa (mobile/tablet)
function MapFalecidosPanel({ falecidos, jazigo, onSelect, onClose }) {
  const formatDate = (d) => {
    if (!d) return "—";
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  };

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 500,
        display: "flex",
        flexDirection: "column",
        background: "#fff",
      }}
    >
      {/* Cabeçalho */}
      <div
        style={{
          background: "linear-gradient(135deg, #F2A66C 0%, #E8956A 100%)",
          padding: "14px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 700,
              fontSize: 15,
              color: "#ffffff",
            }}
          >
            {jazigo?.numero ? `Jazigo ${jazigo.numero}` : "Falecidos do jazigo"}
          </div>
          <div
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 12,
              color: "rgba(255,255,255,0.8)",
              marginTop: 2,
            }}
          >
            {falecidos.length} falecido{falecidos.length !== 1 ? "s" : ""}
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "rgba(255,255,255,0.2)",
            border: "none",
            color: "#fff",
            borderRadius: "50%",
            width: 32,
            height: 32,
            fontSize: 16,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          ✕
        </button>
      </div>

      {/* Lista */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {falecidos.map((falecido, index) => (
          <button
            key={falecido.id ?? index}
            onClick={() => onSelect(falecido)}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              border: "none",
              borderBottom: "1px solid #f0ebe6",
              borderLeft: "4px solid transparent",
              background: index % 2 === 0 ? "rgba(242,166,108,0.1)": "#fff",
              padding: "14px 16px",
              cursor: "pointer",
            }}
          >
            <div
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 700,
                fontSize: 15,
                color: "#2d2d2d",
                lineHeight: 1.3,
              }}
            >
              {falecido.nome_completo}
            </div>
            {falecido.apelido && (
              <div
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13,
                  color: "#999",
                  marginTop: 3,
                  fontStyle: "italic",
                }}
              >
                "{falecido.apelido}"
              </div>
            )}
            <div
              style={{
                display: "flex",
                gap: 20,
                marginTop: 8,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 12,
                color: "#aaa",
              }}
            >
              <span>★ {formatDate(falecido.data_nascimento)}</span>
              <span>✝ {formatDate(falecido.data_falecimento)}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// Página de consulta de sepultados — busca por nome ou data de falecimento via API
function SearchPage({
  navigate,
  user,
  onLogout,
  onSelectFalecido,
  selectedFalecido,
  onFalecidoUpdated,
}) {
  const { isDesktop } = useResponsive();

  // Aba ativa no mobile/tablet: "busca" | "mapa"
  const [activeTab, setActiveTab] = useState("mapa");

  // Estado do tipo de busca selecionado
  const [buscarPor, setBuscarPor] = useState("Nome");

  // Estado do texto digitado no campo de busca
  const [query, setQuery] = useState("");

  // Estado da lista de resultados encontrados
  const [results, setResults] = useState([]);

  // Estado do sepultado selecionado para exibir detalhes
  const [selected, setSelected] = useState(null);

  // Controla o painel retrátil no desktop (no mobile/tablet usa abas)
  const [panelOpen, setPanelOpen] = useState(true);
  const [error, setError] = useState("");

  // Dados de sepultamento, jazigo e fotos do sepultado selecionado
  const [sepultamento, setSepultamento] = useState(null);
  const [jazigo, setJazigo] = useState(null);
  const [jazigoFotos, setJazigoFotos] = useState([]);
  const [loadingJazigo, setLoadingJazigo] = useState(false);
  const [fotoIndex, setFotoIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  // "busca" = modal veio de uma busca por texto; "mapa" = veio de clique no jazigo
  const [modalOrigin, setModalOrigin] = useState("busca");

  // Dados de jazigo por setor
  const [jazigosSetor1, setJazigosSetor1] = useState(null);
  const [jazigosSetor2, setJazigosSetor2] = useState(null);
  const [jazigosSetor3, setJazigosSetor3] = useState(null);
  const [jazigosSetor4, setJazigosSetor4] = useState(null);

  // Estado que exibe os sepultados do jazigo clicado no mapa
  const [selectedId, setSelectedId] = useState(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);

  useEffect(() => {
    const fetchSetores = async () => {
      try {
        const [s1, s2, s3, s4] = await Promise.all([
          Api.getJazigosSetor(1),
          Api.getJazigosSetor(2),
          Api.getJazigosSetor(3),
          Api.getJazigosSetor(4),
        ]);

        setJazigosSetor1(s1);
        setJazigosSetor2(s2);
        setJazigosSetor3(s3);
        setJazigosSetor4(s4);
      } catch {
        setError("Erro ao carregar setores.");
      }
    };

    fetchSetores();
  }, []);

  useEffect(() => {
    if (selectedFalecido) setSelected(selectedFalecido);
  }, [selectedFalecido]);

  // Quando um falecido é selecionado (busca ou clique no mapa), vai para a aba Busca
  useEffect(() => {
    if (selected && !isDesktop) setActiveTab("busca");
  }, [selected, isDesktop]);

  // Ao voltar para a aba Mapa, avisa o Leaflet que o container mudou de tamanho
  useEffect(() => {
    if (activeTab === "mapa" && !isDesktop) {
      const t = setTimeout(() => window.dispatchEvent(new Event("resize")), 80);
      return () => clearTimeout(t);
    }
  }, [activeTab, isDesktop]);

  // Ao selecionar um falecido, busca o sepultamento, o jazigo e as fotos
  useEffect(() => {
    if (!selected) {
      setSepultamento(null);
      setJazigo(null);
      setJazigoFotos([]);
      setFotoIndex(0);
      return;
    }
    setLoadingJazigo(true);
    setSepultamento(null);
    setJazigo(null);
    setJazigoFotos([]);
    setFotoIndex(0);
    Api.getSepultamentosByFalecido(selected.id)
      .then((sepultamentos) => {
        const sep = sepultamentos[0];
        if (!sep?.jazigo_id) {
          setLoadingJazigo(false);
          return;
        }

        setSepultamento(sep);
        return Promise.all([
          Api.getJazigo(sep.jazigo_id),
          Api.getJazigoFotos(sep.jazigo_id),
        ]).then(([jaz, fotos]) => {
          setJazigo(jaz);
          setJazigoFotos(fotos);
          setLoadingJazigo(false);
        });
      })
      .catch(() => setLoadingJazigo(false));
  }, [selected]);

  // Ao selecionar um jazigo no mapa, busca os falecidos ativos sepultados nele e exibe no modal
  useEffect(() => {
    if (!selectedId) return;
    setLoadingJazigo(true);

    Api.getJazigo(selectedId).then((jazigo) => {
      setJazigo(jazigo);
    });

    Api.getJazigoFalecidosAtivos(selectedId)
      .then((falecidos) => {
        setResults(falecidos);
        setSelected(null);
        setModalOrigin("mapa");
        setModalOpen(true);
        setLoadingJazigo(false);
      })
      .catch(() => {
        setLoadingJazigo(false);
      });
  }, [selectedId]);

  const memorialUrl = selected
    ? `${window.location.origin}${window.location.pathname}?page=memorial&selectedFalecido=${selected.id}`
    : "";

  // Formata data de YYYY-MM-DD para DD/MM/YYYY
  const formatDate = (d) => {
    if (!d) return "—";
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  };

  // Filtra os dados localmente por nome ou data; busca via API por código de jazigo
  const handleSearch = () => {
    setError("");
    const q = query.toLowerCase().trim();
    if (!q) {
      setResults([]);
      setSelected(null);
      return;
    }
    const openModal = (found, errorMessage) => {
      setError("");
      setResults(found);
      setSelected(null);
      if (found.length > 0) {
        setModalOrigin("busca");
        setModalOpen(true);
      } else setError(errorMessage);
    };

    if (buscarPor === "Nome") {
      Api.getFalecidosPorNome(q).then((found) => {
        openModal(found, `Nenhum falecido encontrado com o nome ${q}`);
      });
    } else if (buscarPor === "Apelido") {
      Api.getFalecidosPorApelido(q).then((found) => {
        openModal(found, `Nenhum falecido encontrado com o apelido "${q}"`);
      });
    } else if (buscarPor === "Nome do Pai") {
      Api.getFalecidosPorNomePai(q).then((found) => {
        openModal(found, `Nenhum falecido encontrado com nome do pai "${q}"`);
      });
    } else if (buscarPor === "Nome da Mãe") {
      Api.getFalecidosPorNomeMae(q).then((found) => {
        openModal(found, `Nenhum falecido encontrado com nome da mãe "${q}"`);
      });
    } else if (buscarPor === "Data de Falecimento") {
      Api.getFalecidosPorData(q).then((found) => {
        openModal(found, `Nenhum falecido encontrado na data ${q}`);
      });
    } else {
      // Busca por código do jazigo: localiza o jazigo pelo código e retorna seus falecidos ativos
      Api.getJazigos()
        .then((geojson) => {
          const feature = geojson.features?.find(
            (f) => f.properties?.numero?.toLowerCase() === q,
          );
          if (!feature) {
            setResults([]);
            setSelected(null);
            return;
          }
          setJazigo(feature.properties);
          return Api.getJazigoFalecidosAtivos(feature.properties.id);
        })
        .then((falecidos) => {
          if (falecidos)
            openModal(
              falecidos,
              `Nenhum falecido encontrado para o jazigo ${q}`,
            );
          else setError(`Nenhum falecido encontrado para o jazigo ${q}`);
        })
        .catch(() => {
          setResults([]);
          setSelected(null);
        });
    }

    setQuery("");
  };

  // Componente auxiliar — linha da tabela de dados do sepultado
  const TableRow = ({ label, value }) => (
    <div style={{ display: "flex", borderBottom: "1px solid #f0ebe6" }}>
      <div
        style={{
          width: 180,
          padding: "12px 16px",
          background: "linear-gradient(135deg, #F2A66C 0%, #E8956A 100%)",
          color: "#fff",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          flex: 1,
          padding: "12px 16px",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 14,
          color: "#444",
          background: "#fff",
        }}
      >
        {value}
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#fafafa" }}>
      <Header
        onLogin={() => navigate("login")}
        onHome={() => navigate("search")}
        user={user}
        onLogout={onLogout}
        navigate={navigate}
        onFalecidoUpdated={onFalecidoUpdated}
      />

      {/* Tab bar — só mobile/tablet */}
      {!isDesktop && (
        <div style={{ display: "flex", height: 44, borderBottom: "1px solid #e8e0d8", background: "#fff", flexShrink: 0 }}>
          {[
            { id: "busca", label: "Busca" },
            { id: "mapa",  label: "Mapa"  },
          ].map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                flex: 1,
                border: "none",
                borderBottom: activeTab === id ? "3px solid #E8956A" : "3px solid transparent",
                background: "#fff",
                color: activeTab === id ? "#E8956A" : "#aaa",
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
                transition: "color 0.15s, border-color 0.15s",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: isDesktop ? "row" : "column",
          height: !isDesktop ? "calc(100vh - 62px - 44px)" : "auto",
          minHeight: isDesktop ? "calc(100vh - 72px)" : undefined,
          overflow: !isDesktop ? "hidden" : undefined,
        }}
      >
        {/* Painel — formulário de busca e resultados */}
        <div
          style={{
            display: !isDesktop && activeTab !== "busca" ? "none" : undefined,
            width: isDesktop ? (panelOpen ? 460 : 0) : "100%",
            minWidth: isDesktop ? (panelOpen ? 460 : 0) : undefined,
            height: !isDesktop ? "100%" : undefined,
            padding: isDesktop ? (panelOpen ? "32px 28px" : 0) : "16px 16px",
            overflowY: "auto",
            overflowX: "hidden",
            borderRight: isDesktop && panelOpen ? "1px solid #eee" : "none",
            transition: isDesktop
              ? "width 0.3s ease, min-width 0.3s ease, padding 0.3s ease"
              : undefined,
            flexShrink: 0,
          }}
        >
          <FadeIn>
            <h2
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 26,
                fontWeight: 700,
                color: "#E8956A",
                marginBottom: 24,
                textAlign: "center",
              }}
            >
              Consulta de sepultado
            </h2>
          </FadeIn>

          <FadeIn delay={100}>
            <label
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 13,
                color: "#E8956A",
                fontWeight: 500,
                display: "block",
                marginBottom: 6,
              }}
            >
              Buscar por:
            </label>
            <select
              value={buscarPor}
              onChange={(e) => {
                setBuscarPor(e.target.value);
                setQuery("");
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 8,
                border: "1px solid #E8956A",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 14,
                color: "#555",
                background: "#fff",
                marginBottom: 16,
                outline: "none",
                cursor: "pointer",
              }}
            >
              <option>Nome</option>
              <option>Apelido</option>
              <option>Nome do Pai</option>
              <option>Nome da Mãe</option>
              <option>Data de Falecimento</option>
              <option>Jazigo</option>
            </select>
          </FadeIn>

          <FadeIn delay={200}>
            <label
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 13,
                color: "#E8956A",
                fontWeight: 500,
                display: "block",
                marginBottom: 6,
              }}
            >
              {buscarPor === "Nome"
                ? "Digite o nome:"
                : buscarPor === "Apelido"
                  ? "Digite o apelido:"
                  : buscarPor === "Nome do Pai"
                    ? "Digite o nome do pai:"
                    : buscarPor === "Nome da Mãe"
                      ? "Digite o nome da mãe:"
                      : buscarPor === "Jazigo"
                        ? "Código do jazigo:"
                        : "Data (DD/MM/AAAA):"}
            </label>
            <div style={{ display: "flex", gap: 0, marginBottom: 16 }}>
              {buscarPor === "Data de Falecimento" ? (
                <DateInput
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  containerStyle={{ flex: 1 }}
                  style={{
                    padding: "12px 16px",
                    borderRadius: "8px 0 0 8px",
                    border: "1px solid #E8956A",
                    borderRight: "none",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 14,
                    background: "#fff",
                    color: "#333",
                  }}
                />
              ) : (
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    borderRadius: "8px 0 0 8px",
                    border: "1px solid #E8956A",
                    borderRight: "none",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 14,
                    outline: "none",
                  }}
                />
              )}
              <button
                onClick={handleSearch}
                style={{
                  padding: "12px 18px",
                  borderRadius: "0 8px 8px 0",
                  border: "none",
                  background:
                    "linear-gradient(135deg, #F2A66C 0%, #E8956A 100%)",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 16,
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            </div>
            {error && (
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13,
                  color: "#e53935",
                  textAlign: "center",
                  marginBottom: 12,
                }}
              >
                {error}
              </p>
            )}
          </FadeIn>

          {/* Indicador do sepultado selecionado via modal */}
          {selected && (
            <FadeIn delay={100}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 16,
                }}
              >
                <span
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 13,
                    color: "#888",
                  }}
                >
                  Selecionado:
                </span>
                <button
                  onClick={() => setModalOpen(true)}
                  style={{
                    padding: "8px 20px",
                    borderRadius: 24,
                    border: "2px solid #E8956A",
                    background:
                      "linear-gradient(135deg, #F2A66C 0%, #E8956A 100%)",
                    color: "#fff",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {selected.nome_completo}
                </button>
              </div>
            </FadeIn>
          )}

          <FalecidosModal
            isOpen={modalOpen && (isDesktop || modalOrigin === "busca")}
            onClose={() => setModalOpen(false)}
            falecidos={results}
            onSelect={(falecido) => setSelected(falecido)}
          />

          {/* Tabela de dados do sepultado selecionado */}
          {selected && (
            <FadeIn delay={150}>
              <div
                style={{
                  border: "1px solid #E8956A",
                  borderRadius: 10,
                  overflow: "hidden",
                  marginBottom: 24,
                }}
              >
                <div
                  style={{
                    padding: "12px 16px",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#555",
                    background: "#faf6f1",
                    borderBottom: "1px solid #e0d6cc",
                  }}
                >
                  Dados do sepultado
                </div>
                <TableRow
                  label="Nome completo"
                  value={selected.nome_completo || "-"}
                />
                {selected?.apelido && (
                  <TableRow label="Apelido" value={selected.apelido || "-"} />
                )}
                
                {user &&  selected.nome_pai && (
                  <TableRow label="Nome do pai" value={selected.nome_pai || "-"} />
                )}
                {user && selected.nome_mae && (
                  <TableRow label="Nome da mãe" value={selected.nome_mae || "-"} />
                )}
                {selected?.data_nascimento && (
                  <TableRow
                    label="Data de nascimento"
                    value={formatDate(selected.data_nascimento)}
                  />
                )}
                <TableRow
                  label="Data de falecimento"
                  value={formatDate(selected.data_falecimento)}
                />
                <TableRow label="Setor" value={jazigo?.quadra || "-"} />
                <TableRow label="Jazigo" value={jazigo?.numero || "-"} />
                {user && sepultamento?.responsavel_sepultamento && (
                  <TableRow
                    label="Responsável pelo sepultamento"
                    value={sepultamento?.responsavel_sepultamento}
                  />
                )}

                {user && (
                  <TableRow
                    label="Jazigo Público"
                    value={
                      jazigo?.publico == null
                        ? "-"
                        : jazigo.publico
                          ? "Sim"
                          : "Não"
                    }
                  />
                )}

                {user && jazigo?.publico === false && (
                  <TableRow
                    label="Telefone do Responsável"
                    value={jazigo?.telefone_responsavel || "-"}
                  />
                )}

                {user && selected?.causa_morte && (
                  <TableRow
                    label="Causa da morte"
                    value={selected.causa_morte}
                  />
                )}

                {user && selected?.medico && (
                  <TableRow
                    label="Médico responsável"
                    value={selected.medico}
                  />
                )}

                {user && sepultamento?.funeraria && (
                  <TableRow
                    label="Funerária Responsável"
                    value={sepultamento.funeraria}
                  />
                )}
              </div>

              {/* Foto(s) do jazigo */}
              <div style={{ marginBottom: 16 }}>
                {loadingJazigo ? (
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#aaa", textAlign: "center" }}>
                    Carregando jazigo...
                  </p>
                ) : jazigo?.id ? (
                  <JazigoCarousel
                    fotos={jazigoFotos}
                    index={fotoIndex}
                    setIndex={setFotoIndex}
                    setFotos={setJazigoFotos}
                    jazigoId={jazigo.id}
                    user={user}
                  />
                ) : (
                  <img
                    src="/jazigo.jpg"
                    alt="Jazigo"
                    style={{ width: "100%", maxHeight: 280, borderRadius: 10, objectFit: "cover", display: "block" }}
                  />
                )}
              </div>

              {/* Botão para ver homenagem — navega para a página memorial */}
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <OrangeButton
                  onClick={() =>
                    window.open(memorialUrl, "_blank", "noreferrer")
                  }
                >
                  Ver Homenagem
                </OrangeButton>
                {user && (
                  <OrangeButton
                    onClick={() =>
                      navigate('register-buried', { selectedFalecido: selected })
                    }
                  >
                    Editar Falecido
                  </OrangeButton>
                )}
                {user && (
                  <OrangeButton
                    onClick={() =>
                      navigate('register-jazigo', { selectedFalecido: selected })
                    }
                  >
                    Editar Jazigo
                  </OrangeButton>
                )}

                {user && (
                  <OrangeButton
                    onClick={() =>
                      navigate('update-burial', { selectedFalecido: selected })
                    }
                  >
                    Editar Sepultamento
                  </OrangeButton>
                )}
                <button
                  onClick={() => setQrModalOpen(true)}
                  style={{
                    width: "100%",
                    maxWidth: 360,
                    padding: "16px 0",
                    borderRadius: 40,
                    border: "2px solid #E8956A",
                    background: "#fff",
                    color: "#E8956A",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 18,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  QR Code
                </button>
              </div>
            </FadeIn>
          )}
        </div>

        {/* Barra retrátil — só no desktop */}
        {isDesktop && (
          <div
            onClick={() => setPanelOpen((prev) => !prev)}
            style={{
              width: 24,
              minWidth: 24,
              background: "linear-gradient(180deg, #F2A66C 0%, #E8956A 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              userSelect: "none",
              transition: "background 0.2s ease",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background =
                "linear-gradient(180deg, #e89a5e 0%, #d4845a 100%)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background =
                "linear-gradient(180deg, #F2A66C 0%, #E8956A 100%)";
            }}
          >
            <span style={{ color: "#fff", fontSize: 18, fontWeight: 700, lineHeight: 1 }}>
              {panelOpen ? "‹" : "›"}
            </span>
          </div>
        )}

        {/* Mapa do cemitério */}
        <div
          style={{
            display: !isDesktop && activeTab !== "mapa" ? "none" : undefined,
            flex: 1,
            position: "relative",
            background: "#e8e0d8",
            minHeight: 0,
          }}
        >
          <LeafletMap
            setor1={jazigosSetor1}
            setor2={jazigosSetor2}
            setor3={jazigosSetor3}
            setor4={jazigosSetor4}
            setSelectedId={setSelectedId}
            highlightedId={jazigo?.id || 0}
            onDeselect={() => {
              setJazigo(null);
              setSelected(null);
            }}
          />

          {/* Painel de falecidos sobreposto ao mapa — apenas mobile/tablet, origem mapa */}
          {!isDesktop && modalOpen && modalOrigin === "mapa" && results.length > 0 && (
            <MapFalecidosPanel
              falecidos={results}
              jazigo={jazigo}
              onSelect={(falecido) => {
                setSelected(falecido);
                setModalOpen(false);
                setActiveTab("busca");
              }}
              onClose={() => setModalOpen(false)}
            />
          )}
        </div>
      </div>

      {qrModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 14,
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
              padding: "32px 36px",
              maxWidth: 380,
              width: "90%",
              textAlign: "center",
            }}
          >
            <h3
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 18,
                fontWeight: 700,
                color: "#2d2d2d",
                marginBottom: 4,
              }}
            >
              QR Code — Homenagem
            </h3>
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 14,
                color: "#888",
                marginBottom: 20,
              }}
            >
              {selected?.nome_completo}
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 16,
              }}
            >
              <QRCodeSVG value={memorialUrl} size={200} />
            </div>
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 11,
                color: "#bbb",
                wordBreak: "break-all",
                marginBottom: 20,
              }}
            >
              {memorialUrl}
            </p>
            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={() => navigator.clipboard.writeText(memorialUrl)}
                style={{
                  padding: "10px 18px",
                  borderRadius: 8,
                  border: "1px solid #ddd",
                  background: "#f5f5f5",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 14,
                  cursor: "pointer",
                  color: "#555",
                }}
              >
                Copiar link
              </button>
              <a
                href={memorialUrl}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: "none" }}
              >
                <button
                  style={{
                    padding: "10px 18px",
                    borderRadius: 8,
                    border: "2px solid #E8956A",
                    background: "#fff",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 14,
                    cursor: "pointer",
                    color: "#E8956A",
                    fontWeight: 600,
                  }}
                >
                  Abrir
                </button>
              </a>
              <button
                onClick={() => setQrModalOpen(false)}
                style={{
                  padding: "10px 18px",
                  borderRadius: 8,
                  border: "none",
                  background:
                    "linear-gradient(135deg, #F2A66C 0%, #E8956A 100%)",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 14,
                  cursor: "pointer",
                  color: "#fff",
                  fontWeight: 600,
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchPage;
